import { useEffect, useState } from 'react';
import AtlasIcon from './AtlasIcon';
import AtlasEmptyState from './AtlasEmptyState';
import { ONBOARDING_COPY } from './onboardingCopy';
import AvButton from '../nedi/AvButton';
import { t } from '../../i18n';
import NediRowButton from '../nedi/NediRowButton';
import '../nedi/nediAnatomy.css';
import './Buttons.css';
import './HistoriasWorkspace.css';

// Índice de workspace reusável (fases 4/5 + Épico 29 + Nedi 2.3).
// Com `renderPanel`, o detalhe abre inline (voltar) em vez de modal tela cheia.
function HistoriasWorkspace({
  stories = [],
  eyebrow,
  title,
  subtitle,
  ariaLabel,
  resetToken = 0,
  activeStoryKey: controlledStoryKey,
  onActiveStoryChange,
  onImportData,
}) {
  const resolvedEyebrow = eyebrow ?? t('historias.header.eyebrow');
  const resolvedTitle = title ?? t('historias.header.title');
  const resolvedSubtitle = subtitle ?? t('historias.header.subtitle');
  const resolvedAria = ariaLabel ?? t('workspace.historias.a11y');
  const [internalStoryKey, setInternalStoryKey] = useState(null);
  const activeKey = controlledStoryKey !== undefined ? controlledStoryKey : internalStoryKey;

  const setActiveKey = (key) => {
    if (onActiveStoryChange) onActiveStoryChange(key);
    else setInternalStoryKey(key);
  };

  useEffect(() => {
    if (controlledStoryKey === undefined) setInternalStoryKey(null);
  }, [resetToken, controlledStoryKey]);

  const active = stories.find((story) => story.key === activeKey);
  const goBack = () => setActiveKey(null);

  if (active?.renderPanel) {
    return (
      <div className="av-historias av-historias--detail" aria-label={t('historias.detail.a11y')}>
        <div className="av-historias-detail-bar">
          <AvButton
            type="button"
            variant="ghost"
            className="av-historias-back"
            onClick={goBack}
          >
            <AtlasIcon name="chevron-left" size={16} />
            <span>{t('action.back')}</span>
          </AvButton>
          <span className="av-historias-detail-title">{active.title}</span>
        </div>
        <div className="av-historias-detail-body">
          {active.renderPanel({ onBack: goBack })}
        </div>
      </div>
    );
  }

  return (
    <div className="av-historias" aria-label={resolvedAria} data-nedi-workspace="historias">
      <header className="nedi-workspace-header av-historias-header">
        <span className="nedi-workspace-header__eyebrow av-eyebrow">{resolvedEyebrow}</span>
        <h2 className="nedi-workspace-header__title av-historias-title">{resolvedTitle}</h2>
        <p className="nedi-workspace-header__sub av-historias-sub">{resolvedSubtitle}</p>
      </header>

      {stories.length === 0 ? (
        <div className="av-historias-empty">
          <AtlasEmptyState
            {...ONBOARDING_COPY.historiasEmpty}
            actionLabel={onImportData ? ONBOARDING_COPY.historiasEmpty.cta : undefined}
            onAction={onImportData}
          />
        </div>
      ) : (
        <div className="nedi-row-stack av-historias-grid">
          {stories.map((story) => {
            const canOpen = Boolean(story.renderPanel || story.onOpen);
            return (
              <NediRowButton
                key={story.key}
                className="av-historias-card"
                title={story.title}
                subtitle={story.description}
                disabled={!canOpen}
                leading={
                  story.iconName ? (
                    <AtlasIcon name={story.iconName} size={22} />
                  ) : (
                    story.icon
                  )
                }
                onClick={() => {
                  if (story.renderPanel) {
                    setActiveKey(story.key);
                    return;
                  }
                  story.onOpen?.();
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default HistoriasWorkspace;
